import { v4 as uuidv4 } from 'uuid';
import dagre from 'dagre';

// Category metadata — descriptions shown in the category browser
const CATEGORIES = [
  { key: 'Input', label: 'Input', description: 'Read data from external sources', icon: 'core' },
  { key: 'Output', label: 'Output', description: 'Send data to external services', icon: 'core' },
  { key: 'Transform', label: 'Transform', description: 'Manipulate, filter or convert data', icon: 'transform' },
  { key: 'Triggers', label: 'Triggers', description: 'Start your workflow', icon: 'trigger' },
  { key: 'Schedule', label: 'Schedule', description: 'Run workflows on a timer', icon: 'trigger' },
  { key: 'organization', label: 'Organisation', description: 'Organise and structure workflows', icon: 'flow' },
  { key: 'Other', label: 'Other', description: 'Other available nodes', icon: 'misc' },
];

const CATEGORY_ORDER = CATEGORIES.map((c) => c.key);
const CATEGORY_META = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));

export { CATEGORY_ORDER, CATEGORY_META };

export const getGroupedNodeTypes = (registry) => {
  const groupMap = {};

  for (const nodeType of registry) {
    const cat = nodeType.category || 'Other';
    if (!groupMap[cat]) {
      const meta = CATEGORY_META[cat];
      groupMap[cat] = {
        label: meta?.label || cat,
        description: meta?.description || '',
        icon: meta?.icon || 'misc',
        types: [],
      };
    }
    groupMap[cat].types.push(nodeType);
  }

  // Sort nodes alphabetically within each group
  for (const group of Object.values(groupMap)) {
    group.types.sort((a, b) => a.label.localeCompare(b.label));
  }

  // Sort categories alphabetically
  const ordered = {};
  const sortedKeys = Object.keys(groupMap).sort((a, b) => a.localeCompare(b));
  for (const cat of sortedKeys) {
    ordered[cat] = groupMap[cat];
  }

  return ordered;
};

export const generateUniqueNodeName = (baseName, existingNames) => {
  if (!existingNames.includes(baseName)) return baseName;
  let i = 1;
  while (existingNames.includes(`${baseName} ${i}`)) i++;
  return `${baseName} ${i}`;
};

export const createNewN8nNode = (typeKey, existingNames = [], position = [0, 0], registry) => {
  const reg = registry.find((t) => t.key === typeKey);
  if (!reg) throw new Error(`Unknown node type: ${typeKey}`);

  return {
    id: uuidv4(),
    name: generateUniqueNodeName(reg.label, existingNames),
    type: reg.type,
    typeVersion: reg.typeVersion,
    position,
    parameters: { ...reg.defaultParameters },
  };
};

export const getNodeTypeInfo = (type, registry) => {
  return registry.find((t) => t.key === type || t.type === type) || null;
};

// --- React Flow <-> n8n conversion ---

const NODE_WIDTH = 160;
const NODE_HEIGHT = 100;

export const getLayoutedElements = (nodes, edges) => {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'LR', nodesep: 80, ranksep: 120 });

  nodes.forEach((node) => {
    g.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });

  edges.forEach((edge) => {
    g.setEdge(edge.source, edge.target);
  });

  dagre.layout(g);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = g.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - NODE_WIDTH / 2,
        y: nodeWithPosition.y - NODE_HEIGHT / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

export const getIntegration = (nodeType) => {
  const t = (nodeType || '').toLowerCase();
  if (t.includes('felloh')) return 'felloh';
  if (t.includes('pipedrive')) return 'pipedrive';
  if (t.includes('xero')) return 'xero';
  if (t.includes('slack')) return 'slack';
  if (t.includes('email') || t.includes('send_email')) return 'email';
  if (t.includes('sage')) return 'sage';
  return null;
};

export const convertN8nToFlow = (n8nNodes, connections, options = {}) => {
  if (!n8nNodes || n8nNodes.length === 0) return { nodes: [], edges: [] };
  const { layout = false } = options;

  const nodes = [];
  const edges = [];
  const nodeByName = {};

  n8nNodes.forEach((node) => { nodeByName[node.name] = node; });

  n8nNodes.forEach((node) => {
    if (node.disabled) return;
    nodes.push({
      id: node.id,
      type: 'editableStepNode',
      position: node.position ? { x: node.position[0] || node.position.x || 0, y: node.position[1] || node.position.y || 0 } : { x: 0, y: 0 },
      data: { node: { ...node, integration: getIntegration(node.type) } },
    });
  });

  Object.entries(connections || {}).forEach(([sourceName, connectionData]) => {
    const sourceNode = nodeByName[sourceName];
    if (!sourceNode || sourceNode.disabled) return;

    if (connectionData?.main) {
      connectionData.main.forEach((outputs, outputIndex) => {
        if (!outputs || !Array.isArray(outputs)) return;
        outputs.forEach((conn) => {
          const targetNode = nodeByName[conn.node];
          if (!targetNode || targetNode.disabled) return;

          const isMultiOutput = sourceNode.type?.includes('if') || sourceNode.type?.includes('switch');
          let edgeColor = '#6b7280';
          if (isMultiOutput) edgeColor = outputIndex === 0 ? '#10b981' : '#ef4444';

          const edgeId = `e-${sourceNode.id}-${outputIndex}-${targetNode.id}`;
          if (!edges.find((e) => e.id === edgeId)) {
            const edge = {
              id: edgeId,
              source: sourceNode.id,
              target: targetNode.id,
              type: 'smoothstep',
              style: { stroke: edgeColor, strokeWidth: 2 },
            };

            if (isMultiOutput) {
              edge.sourceHandle = `source-${outputIndex}`;
              edge.label = outputIndex === 0 ? 'true' : 'false';
            }

            edges.push(edge);
          }
        });
      });
    }
  });

  return layout ? getLayoutedElements(nodes, edges) : { nodes, edges };
};

export const convertFlowToN8n = (rfNodes, rfEdges, draftNodes) => {
  // Build nodes from draft (source of truth), update positions from RF
  const nodeById = {};
  draftNodes.forEach((n) => { nodeById[n.id] = n; });

  const nodes = rfNodes.map((rfNode) => {
    const original = nodeById[rfNode.id] || rfNode.data?.node || {};
    return {
      ...original,
      position: [Math.round(rfNode.position.x), Math.round(rfNode.position.y)],
    };
  });

  // Build connections keyed by source node name
  const nodeIdToName = {};
  nodes.forEach((n) => { nodeIdToName[n.id] = n.name; });

  const connections = {};
  rfEdges.forEach((edge) => {
    const sourceName = nodeIdToName[edge.source];
    if (!sourceName) return;
    const targetName = nodeIdToName[edge.target];
    if (!targetName) return;

    const outputIndex = edge.sourceHandle ? parseInt(edge.sourceHandle.replace('source-', ''), 10) : 0;

    if (!connections[sourceName]) connections[sourceName] = { main: [] };
    while (connections[sourceName].main.length <= outputIndex) {
      connections[sourceName].main.push([]);
    }
    connections[sourceName].main[outputIndex].push({
      node: targetName,
      type: 'main',
      index: 0,
    });
  });

  return { nodes, connections };
};
